import type { UppyFileMeta } from '@unifyapps/carbon/hooks/useUppy';
import { FILE_VISIBILITY_TO_ACCESS_SCOPE } from '@unifyapps/defs/types/fileAccessScope';
import type { FileVisibility } from '@unifyapps/defs/types/fileVisibility';

/**
 * The single frontend -> backend boundary for file permissions.
 *
 * Callers pick a FileVisibility (the builder-facing construct); this returns the upload
 * meta the backend actually reads. Nothing else should build an `accessScope` by hand —
 * route new upload surfaces through here so they all express permissions the same way.
 *
 * Storage-specific translation is deliberately NOT here: the uppy instance knows which
 * provider it is talking to and flips `publicAcl` for S3 itself (see getUppyInstance),
 * so callers never have to hold a provider just to describe who may read a file.
 *
 * `isBuilderAsset` is an orthogonal axis — "is this a standard entity changesets can move
 * between environments" — not a permission. It is passed explicitly by the surfaces
 * that want it rather than inferred from visibility.
 */
export function getFileUploadMeta(
  fileVisibility: FileVisibility | undefined,
  isBuilderAsset?: boolean,
): UppyFileMeta {
  const meta: UppyFileMeta = {};

  if (fileVisibility) {
    meta.accessScope = FILE_VISIBILITY_TO_ACCESS_SCOPE[fileVisibility];
  }

  if (isBuilderAsset !== undefined) {
    meta.isBuilderAsset = isBuilderAsset;
  }

  return meta;
}
